import React, { Fragment, useEffect, useState } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';

import { requestUpdate, requestInform as request } from 'api/user';
import Content from 'components/common-sfims/Content';
import List from 'components/common-sfims/List';
import Valid from 'components/pages/Valid';
import { onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import useLoginState from 'global/loginState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_NAME } from 'types/const';
import { RETURN_YN } from 'types/type';

/**
 * 마이 페이지
 */
const MyPage = () => {
  /* variable */
  const { seq } = getLoginInform();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const { setLogin } = useLoginState();
  const {
    plant: { alls: plants }
  } = usePlantState();

  /* state */
  // 사용자 정보
  const [inform, setInform] = useObjReducer({});
  // 수정 비활성화 여부
  const [isDisableUpdate, setIsDisableUpdate] = useState(false);
  // 패스워드 재확인 여부
  const [isValid, setIsValid] = useState(false);
  // 패스워드 확인
  const [passwordConfirm, setPasswordConfirm] = useState('');
  // 사용자 수정 요청 데이터
  const [userData, setUserData] = useObjReducer({
    email: '',
    userPwd: '',
    factoryCodeSeq: 0
  });

  /* watch */
  useEffect(() => {
    requestInform();
  }, []);

  /* method */
  // 사용자 정보 조회 요청
  const requestInform = async () => {
    try {
      const { data = {}, code, message } = await request(seq);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setInform(data);
      setUserData({
        email: data.email ?? '',
        factoryCodeSeq: data.factoryCodeSeq ?? 0
      });
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    }
  };

  /* event */
  // 취소 클릭 시
  const onCancelClick = async () => {
    // 초기화
    await requestInform();
    setUserData({ userPwd: '' });
    setPasswordConfirm('');
  };
  // 설정
  const onSubmit = async event => {
    const data = { ...userData };

    let title = 'completeUpdate';
    let message = 'completeUpdateMessage';

    event.preventDefault();

    setIsDisableUpdate(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(data, ['userPwd'])) {
        throw { code: RESPONSE_CODE.BadRequestUpdate };
      }

      // 패스워드 입력 시 유효성 확인
      if (data.userPwd) {
        if (!/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()])/.test(data.userPwd)) {
          throw { code: 'invalidUserPwd' };
        }

        if (passwordConfirm !== data.userPwd) {
          throw { code: 'userPwdConfirmMessage' };
        }
      }

      const { code, message } = await requestUpdate(seq, data);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      // 로그인 정보 재설정
      setLogin({ fatoryCodeSeq: data.factoryCodeSeq });
    } catch (error) {
      title = 'notice';
      message = getErrorMessage(error?.code, error?.error);
    } finally {
      setConfirmModal({
        isOpen: true,
        title,
        message
      });
      setIsDisableUpdate(false);
    }
  };

  return (
    <>
      {isValid ? (
        <Form onSubmit={onSubmit}>
          <Content>
            <Fragment key="title">{getText(ROUTER_NAME.myPage)}</Fragment>
            <Fragment key="body">
              <Row className="g-3 mb-3">
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>{getText('userId')}</Form.Label>
                    <Form.Control
                      readOnly
                      size="sm"
                      type="text"
                      value={inform.userId}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>{getText('userNm')}</Form.Label>
                    <Form.Control
                      readOnly
                      size="sm"
                      type="text"
                      value={inform.userNm}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>{getText('userBuName')}</Form.Label>
                    <Form.Control
                      readOnly
                      size="sm"
                      type="text"
                      value={inform.buName}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-3 mb-3">
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>* {getText('email')}</Form.Label>
                    <Form.Control
                      size="sm"
                      type="email"
                      value={userData.email}
                      name="email"
                      required
                      onChange={event => onInputChange(event, setUserData)}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>{getText('userPwd')}</Form.Label>
                    <Form.Control
                      size="sm"
                      type="password"
                      value={userData.userPwd}
                      maxLength={16}
                      minLength={8}
                      name="userPwd"
                      onChange={event => onInputChange(event, setUserData)}
                    />
                    <p className="fs--1 text-danger mb-0">
                      {getText('userPwdInform')}
                    </p>
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>{getText('userPwdConfirm')}</Form.Label>
                    <Form.Control
                      size="sm"
                      type="password"
                      value={passwordConfirm}
                      maxLength={16}
                      minLength={8}
                      onChange={({ target: { value } }) => {
                        setPasswordConfirm(value);
                      }}
                    />
                    <p className="fs--1 text-danger mb-0">
                      {getText('userPwdConfirmInform')}
                    </p>
                  </Form.Group>
                </Col>
              </Row>
              <List
                className={{ table: 'mt-3' }}
                columns={[
                  {
                    text: 'pltCode',
                    className: 'w-50'
                  },
                  'dfRead',
                  'readYn',
                  'modYn'
                ]}
              >
                {plants.map(({ seq, pltCode } = {}) => {
                  const { readYn, modYn } =
                    inform.authList?.find?.(
                      ({ pltCode: plant } = {}) => plant === pltCode
                    ) ?? {};

                  return (
                    <tr key={`plants.${pltCode}`}>
                      <td>{pltCode}</td>
                      <td>
                        <Form.Check
                          disabled={readYn !== RETURN_YN.Y}
                          type="radio"
                          checked={seq === userData.factoryCodeSeq}
                          name="factoryCodeSeq"
                          required
                          value={seq}
                          onChange={event => {
                            onInputChange(event, setUserData, {
                              convert: 'number'
                            });
                          }}
                        />
                      </td>
                      <td>
                        <Form.Check disabled checked={readYn === RETURN_YN.Y} />
                      </td>
                      <td>
                        <Form.Check disabled checked={modYn === RETURN_YN.Y} />
                      </td>
                    </tr>
                  );
                })}
              </List>
            </Fragment>
            <Fragment key="footerButton">
              <Button
                size="sm"
                variant="secondary"
                className="ms-auto"
                onClick={onCancelClick}
              >
                {getText('cancel')}
              </Button>
              <Button
                disabled={isDisableUpdate}
                size="sm"
                type="submit"
                className="ms-2"
              >
                {getText('ok')}
              </Button>
            </Fragment>
          </Content>
        </Form>
      ) : (
        <Valid userId={inform.userId} onValid={() => setIsValid(true)} />
      )}
    </>
  );
};

export default MyPage;
